import { forwardRef } from 'react';
import { cn } from '../cn.js';
import { Input, type InputProps } from '../components/input.js';
import { IconSearch } from '../icons.js';

export interface SearchInputProps extends Omit<InputProps, 'type'> {
  /** The accessible name. Required: a magnifying glass is not a label. */
  readonly label: string;
}

/**
 * A search field.
 *
 * `type="search"`, so a browser offers its own clearing affordance and a phone shows a search key
 * rather than a return key. The icon is decorative — the field's name comes from `label`, because
 * a magnifying glass tells a screen reader nothing and placeholder text disappears exactly when
 * somebody is typing into it.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, label, ...props },
  ref,
) {
  return (
    <div className={cn('relative', className)}>
      <IconSearch
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-(--space-sm) size-(--size-icon-sm) -translate-y-1/2 text-(--color-text-muted)"
      />
      <Input
        ref={ref}
        type="search"
        aria-label={label}
        className="pl-(--input-padding-inline-icon)"
        {...props}
      />
    </div>
  );
});
